import {
    Alignment, DatePart, DateRange, DateUtil, DebouncedFunction, DisplayType, HorizontalAlignment,
    NullDateRangeDateBehavior, NumberUtil, StringUtil
} from "@mcleod/core";
import { DataSourceMode, Label, LabelProps, TextboxProps } from "../..";
import { Component } from "../../base/Component";
import { ComponentPropDefinitions } from "../../base/ComponentProps";
import { ComponentTypes } from "../../base/ComponentTypes";
import { ValidationResult } from "../../base/ValidationResult";
import { EnhancedCompoundComponent } from "../compound/EnhancedCompoundComponent";
import { Textbox } from "../textbox/Textbox";
import { DateRangeFilterPropDefinitions, DateRangeFilterProps, EndDateType } from "./DateRangeFilterProps";

enum InnerIds {
    START = "textboxStart",
    END = "textboxEnd",
    DAYS_OUT = "textboxDaysOut",
    DATE_RANGE = "labelDateRange"
}

interface ParsedDateRange {
    startText: string;
    endText: string;
    dateRange: DateRange;
    dateRangeText: string;
    validationWarning?: string;
}

export class DateRangeFilter extends EnhancedCompoundComponent implements DateRangeFilterProps {
    private _nullStartDateBehavior: NullDateRangeDateBehavior;
    private _nullEndDateBehavior: NullDateRangeDateBehavior;
    private _endDateType: EndDateType;
    private _textboxStart: Textbox;
    private _textboxDaysOut: Textbox;
    private _textboxEndDate: Textbox;
    private _labelDateRange: Label;
    private _parsedDateRange: ParsedDateRange;
    private _dateRangeLabelAlign: Alignment;

    private syncDateRange = () => this.labelDateRange.text = this.parsedDateRange.dateRangeText;
    private dateChangeListener = DebouncedFunction.create(this.syncDateRange, 300);

    constructor(props: Partial<DateRangeFilterProps>) {
        super({ ...props });
        this.setInnerComponentProps(this, { padding: 0});
    }

    override syncInnerComponentIds() {
        this.setInnerComponentId(this.textboxStart, InnerIds.START);
        this.setInnerComponentId(this.textboxEndDate, InnerIds.END);
        this.setInnerComponentId(this.textboxDaysOut, InnerIds.DAYS_OUT);
        this.setInnerComponentId(this.labelDateRange, InnerIds.DATE_RANGE);
    }

    public get nullStartDateBehavior(): NullDateRangeDateBehavior {
        return this._nullStartDateBehavior ?? this.getPropertyDefinitions().nullStartDateBehavior.defaultValue;
    }

    public set nullStartDateBehavior(value: NullDateRangeDateBehavior) {
        this._nullStartDateBehavior = value;
    }

    public get nullEndDateBehavior(): NullDateRangeDateBehavior {
        return this._nullEndDateBehavior ?? this.getPropertyDefinitions().nullEndDateBehavior.defaultValue;
    }

    public set nullEndDateBehavior(value: NullDateRangeDateBehavior) {
        this._nullEndDateBehavior = value;
    }

    public get endDateType(): EndDateType {
        return this._endDateType ?? this.getPropertyDefinitions().endDateType.defaultValue;
    }

    public set endDateType(value: EndDateType) {
        const oldValue = this._endDateType;
        this._endDateType = value;
        if (oldValue !== value) {
            this.syncEndDateType();
        }
    }

    syncEndDateType() {
        if (this.isDeserializing() || this.isCloning()) {
            return;
        }

        if (this.isDaysOutType()) {
            this.textboxDaysOut = new Textbox();
        } else {
            this.textboxEndDate = new Textbox();
        }

        this.syncRangeLabelAlign();
    }

    private getDateTextboxOverrideProps(): Partial<TextboxProps> {
        return {saveParamMode: true, rowBreak: false};
    }

    private getDateTextboxDefaultProps(props?: Partial<TextboxProps>): Partial<TextboxProps> {
        return {displayType: DisplayType.DATE, ...props};
    }

    public get textboxStart(): Textbox {
        return this._textboxStart;
    }

    public set textboxStart(value: Textbox) {
        this.setInternalComponent({
            value,
            propName: "_textboxStart",
            getPropertyDefinitionsOverride: () => DateRangeFilterPropDefinitions.getStartDateTextboxDefinitions(),
            defaultProps: this.getDateTextboxDefaultProps({ caption: "Start Date" }),
            overrideProps: this.getDateTextboxOverrideProps()
        });
    }

    public get textboxEndDate(): Textbox {
        return this._textboxEndDate;
    }

    public set textboxEndDate(value: Textbox) {
        this.setInternalComponent({
            value,
            propName: "_textboxEndDate",
            getPropertyDefinitionsOverride: () => DateRangeFilterPropDefinitions.getEndDateTextboxDefinitions(),
            defaultProps: this.getDateTextboxDefaultProps({ caption: "End Date" }),
            overrideProps: this.getDateTextboxOverrideProps()
        })
    }

    public get textboxDaysOut(): Textbox {
        return this._textboxDaysOut;
    }

    public set textboxDaysOut(value: Textbox) {
        this.setInternalComponent({
            value,
            propName: "_textboxDaysOut",
            getPropertyDefinitionsOverride: () => DateRangeFilterPropDefinitions.getDaysOutTextboxDefinitions(),
            defaultProps: { caption: "Days Out", width: 80 } as Partial<TextboxProps>,
            overrideProps: { displayType: DisplayType.INTEGER, align: HorizontalAlignment.RIGHT, rowBreak: false } as Partial<TextboxProps>
        });
    }

    public get labelDateRange(): Label {
        return this._labelDateRange;
    }

    public set labelDateRange(value: Label) {
        this.setInternalComponent({
            value,
            propName: "_labelDateRange",
            getPropertyDefinitionsOverride: () => DateRangeFilterPropDefinitions.getDateRangeLabelDefinitions(),
            defaultProps: { width: "180", fontBold: true, marginLeft: 4 } as Partial<LabelProps>,
            overrideProps: { minHeight: 24 }
        });
    }

    protected override createAndAddPropsAccessLabel(parentComponent: Component): Label {
        const label = super.createAndAddPropsAccessLabel(parentComponent);
        if (parentComponent === this.labelDateRange) {
            label.setProps({
                width: "100%",
                text: "Date Range Label Props",
                tooltip: "Click to access the properties for the Date Range Label.",
                align: HorizontalAlignment.CENTER,
                minHeight: 24
            });
        }
        return label;
    }


    protected override addInternalComponent(value: Component) {
        if (value == this.textboxEndDate && this.contains(this.textboxDaysOut)) {
            this.replace(this.textboxDaysOut, this.textboxEndDate);
        } else if (value == this.textboxDaysOut && this.contains(this.textboxEndDate)) {
            this.replace(this.textboxEndDate, this.textboxDaysOut);
        } else {
            this.addIfNotPresent(value);
        }
    }

    protected override doAfterSetInternalComponent(_component: Component) {
        this.syncListeners();
    }

    private syncListeners() {
        const addOrRemoveValidationCallback = (textbox: Textbox, addCallback = true) => {
            if (textbox == null) return;

            if (addCallback === true) {
                textbox.addlValidationCallback = (_value: string, mode: DataSourceMode) => {
                    return this.validateRange(textbox, mode);
                };
            } else {
                textbox.addlValidationCallback = null;
            }
        }

        this.textboxStart?.addChangeListener(this.dateChangeListener);
        this.textboxDaysOut?.removeChangeListener(this.dateChangeListener);
        this.textboxEndDate?.removeChangeListener(this.dateChangeListener);
        addOrRemoveValidationCallback(this.textboxStart, false);
        addOrRemoveValidationCallback(this.textboxEndDate, false);

        if (this.isDaysOutType()) {
            this.textboxDaysOut?.addChangeListener(this.dateChangeListener);
        } else if (this.textboxStart != null && this.textboxEndDate != null) {
            this.textboxEndDate.addChangeListener(this.dateChangeListener);
            addOrRemoveValidationCallback(this.textboxStart);
            addOrRemoveValidationCallback(this.textboxEndDate);
        }
    }

    private validateRange(component: Component, mode: DataSourceMode): ValidationResult {
        if (mode === DataSourceMode.SEARCH) {
            return null;
        }
        const result: ValidationResult = { isValid: true, component: component };
        if (this.textboxStart.isEmpty() || this.textboxEndDate.isEmpty()) {
            this.syncDateRange();
            return result;
        }

        const otherComp = component === this.textboxStart ? this.textboxEndDate : this.textboxStart;
        if (!StringUtil.isEmptyString(this.parsedDateRange.validationWarning)) {
            result.isValid = false;
            result.validationMessage = this.parsedDateRange.validationWarning;
            if (otherComp.validationWarning == null) {
                otherComp["_dateRangeValidationWarning"] = result.validationMessage;
                otherComp.validationWarning = result.validationMessage;
            }
            return result;
        } else if (otherComp.validationWarning === otherComp["_dateRangeValidationWarning"]) {
            otherComp.validationWarning = null;
        }
        return result;
    }

    private get parsedDateRange(): ParsedDateRange {
        if (this.shouldCreateParsedDateRange()) {
            this._parsedDateRange = this.createParsedDateRange();
        }
        return this._parsedDateRange;
    }

    private shouldCreateParsedDateRange(): boolean {
        if (this._parsedDateRange == null)
            return true;
        // check if the start or end text has changed since last parsed
        const startText = this.textboxStart.text;
        const endText = this.isDaysOutType() ? this.textboxDaysOut.text : this.textboxEndDate.text;
        return startText != this._parsedDateRange.startText || endText != this._parsedDateRange.endText;
    }

    private createParsedDateRange(): ParsedDateRange {
        const range = new DateRange({
            startDate: this.textboxStart.text,
            nullStartDateBehavior: this.nullStartDateBehavior,
            nullEndDateBehavior: this.nullEndDateBehavior
        })

        if (EndDateType.DAYS_OUT === this.endDateType) {
            range.endDate = this.calculateDaysOutEndDate(range.startDate);
        } else {
            range.endDate = this.textboxEndDate.text;
        }

        let rangeString: string;
        let validationWarning: string;
        if (range.startDate == null && this.isStartDateRequired()) {
            rangeString = "";
        } else if (range.endDate == null && this.isEndDateRequired()) {
            rangeString = "";
        } else {
            try {
                range.validate();
                rangeString = range.getFormattedString();
            } catch(error) {
                validationWarning = error.message;
            }
        }

        return {
            startText: this.textboxStart.text,
            endText: this.isDaysOutType() ? this.textboxDaysOut.text : this.textboxEndDate.text,
            dateRange: range,
            dateRangeText: rangeString,
            validationWarning: validationWarning
        };
    }

    private calculateDaysOutEndDate(startDate: Date): Date {
        if (this.textboxDaysOut.isEmpty()) {
            return null;
        }

        if (startDate == null) {
            startDate = new Date();
        }

        if (!DateUtil.isDateValid(startDate)) {
            return null;
        }

        let daysToAdd: number;
        try {
            daysToAdd = NumberUtil.parseIntStrict(this.textboxDaysOut.text);
            return DateUtil.dateAdd(DatePart.DAY, startDate, daysToAdd);
        } catch (e) {
            return null;
        }
    }

    public get startDate(): Date {
        return this.parsedDateRange?.dateRange?.startDate;
    }

    public get endDate(): Date {
        return this.parsedDateRange?.dateRange?.endDate;
    }

    private isDaysOutType(): boolean {
        return EndDateType.DAYS_OUT === this.endDateType;
    }

    private isStartDateRequired(): boolean {
        return this.textboxStart.required;
    }

    private isEndDateRequired(): boolean {
       return this.isDaysOutType() ? this.textboxDaysOut.required : this.textboxEndDate.required;
    }

    override getPropertyDefinitions(): ComponentPropDefinitions {
        return DateRangeFilterPropDefinitions.getDefinitions();
    }

    public get dateRangeLabelAlign(): Alignment {
        return this._dateRangeLabelAlign ?? this.getPropertyDefinitions().dateRangeLabelAlign.defaultValue;
    }

    public set dateRangeLabelAlign(value: Alignment) {
        if (this._dateRangeLabelAlign === value)
            return;
        this._dateRangeLabelAlign = value;
        this.syncRangeLabelAlign();
    }

    private syncRangeLabelAlign() {
        this.setInnerComponentProps(this.components[1], { rowBreak: this.dateRangeLabelAlign === Alignment.BOTTOM });
        this.setInnerComponentProps(this.labelDateRange, { marginTop: this.dateRangeLabelAlign === Alignment.RIGHT ? 24 : null});
    }


    protected _initialDropInDesigner() {
        super._initialDropInDesigner();
        this.textboxStart = new Textbox();
        this.syncEndDateType();
        this.labelDateRange = new Label();
    }

    /**
     * Identify the inner textbox and label components
     */
    protected override identifyInnerComponents() {
        if (this.components.length !== 3 ) {
            throw new Error("DateRangeFilter must have exactly 3 inner components.");
        }

        if (!(this.components[0] instanceof Textbox) || !(this.components[1] instanceof Textbox)){
            throw new Error("First and second inner components must be Textbox components.");
        }

        if (this.components.length === 3 && !(this.components[2] instanceof Label)) {
            throw new Error("Third inner component must be a Label component.");
        }

        this.textboxStart = this.components[0] as Textbox;
        if (this.isDaysOutType()) {
            this.textboxDaysOut = this.components[1] as Textbox;
        } else {
           this.textboxEndDate = this.components[1] as Textbox;
        }
        this.labelDateRange = this.components[2] as Label;

        this.syncRangeLabelAlign();
    }

    override get serializationName() {
        return "daterangefilter";
    }

    override get properName(): string {
        return "Date Range Filter";
    }
}
ComponentTypes.registerComponentType("daterangefilter", {constructor:DateRangeFilter.prototype.constructor});
